/*eslint sort-keys: ["error", "asc", {caseSensitive: false, natural: true}] -- expect object keys to be sorted*/
/*eslint typescript-sort-keys/interface: ["error", "asc", {"caseSensitive": false, "natural": true,}] -- expect interface keys to be sorted*/

import { isValidCron } from "cron-validator";
import * as R from "runtypes";

import {
  NoPrewarmedImages,
  PrewarmedDefaultInfo,
} from "./customKernels/customKernelTypes";
import { DataConnectionType } from "./dataConnectionTypes.js";
import { StringDate } from "./datetimeType.js";
import {
  BasetenEmbeddingModel,
  DEFAULT_MPK,
  EmbeddingModelProviderKeyType,
  LanceEmbeddingModel,
  LanceEmbeddingProvider,
  OpenAIEmbeddingModel,
} from "./embeddings";
import { ProductionEnvironment } from "./environment";
import { OrgId } from "./idTypeBrands";
import {
  DefaultKernelManagementCPMigrationState,
  KernelManagementCPMigrationState,
} from "./kernel";
import { AnthropicContextEditingConfig } from "./magic/anthropicTypes";
import {
  MagicProviderConfig,
  ModelFeatureConfig,
  ModelFeatureKey,
} from "./MagicTypes";
import {
  FlaggedNewUserFeature,
  FlaggedNewUserFeatureSet,
  NewUserFeatureFlagDefaults,
  isNewUserFeature,
} from "./NewUserFeatures.js";
import { CONSIDER_LAZY_LOADING_SIZE } from "./outputLimits";
import { PermissionedEntityTypeName } from "./PermissionedEntityTypeName.js";
import { typedObjectKeys } from "./utils/typedObjects";

export const RateLimitConfig = R.Record({
  duration: R.Number,
  points: R.Number,
});
export type RateLimitConfig = R.Static<typeof RateLimitConfig>;

export const RateLimitTargetConfig = R.Partial({
  perClient: RateLimitConfig,
  perEnv: RateLimitConfig,
  perOrg: RateLimitConfig,
});
export type RateLimitTargetConfig = R.Static<typeof RateLimitTargetConfig>;

export const AoRateLimitConfig = R.Partial({
  appSession: RateLimitTargetConfig,
  hex: RateLimitTargetConfig,
  hexVersion: RateLimitTargetConfig,
  reviewRequest: RateLimitTargetConfig,
  semanticProjectVersion: RateLimitTargetConfig,
});
export type AoRateLimitConfig = R.Static<typeof AoRateLimitConfig>;

export const WebsocketRateLimitConfig = R.Partial({
  perClientConnect: RateLimitConfig,
});
export type WebsocketRateLimitConfig = R.Static<
  typeof WebsocketRateLimitConfig
>;

export const OpenAiRateLimitConfig = R.Record({
  requests: R.Dictionary(RateLimitConfig, R.String),
  tokens: R.Dictionary(RateLimitConfig, R.String),
});
export type OpenAiRateLimitConfig = R.Static<typeof OpenAiRateLimitConfig>;
export const DEFAULT_OPEN_AI_RATE_LIMIT_CONFIG: OpenAiRateLimitConfig = {
  requests: {
    "open-ai-rpm-limit": {
      duration: 60,
      points: 400,
    },
  },
  tokens: {
    "open-ai-tpd-limit": {
      duration: 86400,
      points: 160_000_000,
    },
    "open-ai-tpm-limit": {
      duration: 60,
      points: 500_000,
    },
  },
};

export type MagicTableFormattingMethod = "YAML_LIKE" | "XML";

/**
 * Configuration for parallel tool calling per agent type.
 * Allows independent control of parallel execution for each agent.
 */
export type AgentParallelToolCallsConfig = {
  askAgent?: boolean;
  logicViewAgent?: boolean;
  semanticAuthoringAgent?: boolean;
};

const DEFAULT_AGENT_PARALLEL_TOOL_CALLS_CONFIG: AgentParallelToolCallsConfig = {
  askAgent: false,
  logicViewAgent: false,
  semanticAuthoringAgent: false,
};

const DEFAULT_AO_RATE_LIMIT_CONFIG: AoRateLimitConfig = {};

export const GarbageCollectionRateLimitConfig = R.Record({
  appSessionAoLogsPerMinute: R.Number,
  appSessionEventLogsPerMinute: R.Number,
  compactedAppSessionEventLogsPerMinute: R.Number,
  executionEventsPerMinute: R.Number,
  hexAoLogsPerMinute: R.Number,
  hexVersionAoLogsPerMinute: R.Number,
  outputContentsPerRun: R.Number,
  outputsGracePeriodMinutes: R.Number,
  outputsPerMinute: R.Number,
  projectStatesPerMinute: R.Number,
  queryCacheEntriesPerMinute: R.Number,
  reviewRequestAoLogsPerMinute: R.Number,
  semanticProjectVersionAoLogsPerMinute: R.Number,
  tableReferencesPerMinute: R.Number,
});
export type GarbageCollectionRateLimitConfig = R.Static<
  typeof GarbageCollectionRateLimitConfig
>;

export const DEFAULT_GARBAGE_COLLECTION_RATE_LIMIT_CONFIG: GarbageCollectionRateLimitConfig =
  {
    appSessionAoLogsPerMinute: 50_000,
    appSessionEventLogsPerMinute: 50_000,
    compactedAppSessionEventLogsPerMinute: 5_000,
    executionEventsPerMinute: 50_000,
    hexAoLogsPerMinute: 10_000,
    hexVersionAoLogsPerMinute: 10_000,
    outputContentsPerRun: 1_000,
    outputsGracePeriodMinutes: 10,
    outputsPerMinute: 10_000,
    projectStatesPerMinute: 10_000,
    queryCacheEntriesPerMinute: 10_000,
    reviewRequestAoLogsPerMinute: 10_000,
    semanticProjectVersionAoLogsPerMinute: 10_000,
    tableReferencesPerMinute: 10_000,
  };

export type StringArrayJson = {
  values: string[];
};

export type ElasticsearchReindexConfig = {
  initialIdOffset: {
    columns?: string;
    databases?: string;
    schemas?: string;
    tables?: string;
  };
};

export type ElasticsearchIndexReaderConfig = {
  datasourceSchemas: string;
  projects: string;
};
type PerIndexConfig = {
  backfillIndexes: string[];
  // deprecated - use ElasticsearchIndexReaderConfig
  readerIndex: string;
  writerIndexes: string[];
};
export type ElasticsearchIndexConfig = {
  datasourceSchemas: PerIndexConfig;
  projects: PerIndexConfig;
};
export const HexToESSyncMode = {
  ALWAYS_COMMIT: "always_commit",
  COMMIT_ONLY: "commit_only",
  DISABLED: "disabled",
  ENABLED: "enabled",
};
export type HexToESSyncMode =
  (typeof HexToESSyncMode)[keyof typeof HexToESSyncMode];
export type QueryConfig = {
  category_and_status_boost?: number;
  creator: {
    weight: number;
  };
  description: {
    boost?: number | null;
  };
  last_updated_ts: {
    decay: number;
    offset: string;
    scale: string;
  };
  title: {
    boost?: number | null;
    // Whole query === whole title
    exactMatchBoost?: number | null;
    // title.startsWith(whole query)
    prefixMatchBoost?: number | null;
  };
};
export const DEFAULT_QUERY_CONFIG: QueryConfig = {
  category_and_status_boost: 1.2,
  creator: {
    weight: 2,
  },
  description: {
    boost: null,
  },
  last_updated_ts: {
    decay: 0.2,
    offset: "14d",
    scale: "90d",
  },
  title: {
    boost: null,
    exactMatchBoost: null,
    prefixMatchBoost: null,
  },
};

export type ComputeHeartbeatSyncToStripeStatus =
  | "JOB_DISABLED" // this queue will not run
  | "PROCESS_BUT_DONT_SEND" // the queue will run to mark compute heartbeats as seen, but none will be sent to stripe
  | "SEND_TO_STRIPE"; // the queue will run and send appropriate compute heartbeats to stripe

export const FastForwardOptions = R.Record({
  bufferSeconds: R.Number,
  debounce: R.Boolean,
  enableRequestCaching: R.Boolean,
  logging: R.Boolean,
  throttleTimeoutMs: R.Number,
});
export type FastForwardOptions = R.Static<typeof FastForwardOptions>;
export const DEFAULT_FAST_FORWARD_IMPLEMENTATION: FastForwardOptions = {
  bufferSeconds: 120,
  debounce: false,
  enableRequestCaching: false,
  logging: false,
  throttleTimeoutMs: 30_000,
};

export const RawFastForwardOptions = R.Partial({
  bufferSeconds: R.Number,
  debounce: R.Boolean,
  enableRequestCaching: R.Boolean,
  logging: R.Boolean,
  throttleTimeoutMs: R.Number,
});
export const RawFastForwardImplementationConfig = R.Partial({
  appSession: RawFastForwardOptions,
  hex: RawFastForwardOptions,
  hexVersion: RawFastForwardOptions,
  reviewRequest: RawFastForwardOptions,
  semanticProjectVersion: RawFastForwardOptions,
});
export type RawFastForwardImplementationConfig = R.Static<
  typeof RawFastForwardImplementationConfig
>;

/**
 * The config for the internal test collection queue that we use to run headless notebooks that are
 * part of special collections on a scheduled cadence.
 */
export type InternalTestCollectionQueue = {
  /**
   * The collection ids we want to run tests on.
   */
  collection_ids: string[];
  /**
   * Whether or not the internal test queue is enabled; including here so that we can disable outside of server restarts.
   */
  enabled: boolean;
  /**
   * How long each project run should have before we declare the run to take too long and kill it manually.
   */
  single_run_timeout_ms: number;
  /**
   * Settings for slack notifications based on queue success and failure.
   */
  slack_settings: {
    /**
     * The ID of the default slack channel to report success and failure messages to.
     * This can be overridden based on collection_id by using the overrides field.
     */
    default_slack_channel_id: string;
    /**
     * Overrides Slack behavior for a particular collectionId.
     */
    overrides: {
      [collectionId: string]: {
        run_as_user_id?: string;
        send_msg_on_success: boolean;
        slack_channel_id: string;
        tag_support_on_failure: boolean;
      };
    };
    /**
     * Whether or not we should send a slack message if the collection succeeds.
     */
    send_slack_on_success: boolean;
  };
};
export const DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE = {
  collection_ids: [],
  cron: "* 0 0 0 0",
  enabled: false,
  single_run_timeout_ms: 120_000,
  slack_settings: {
    default_slack_channel_id: "",
    overrides: {},
    send_slack_on_success: false,
  },
};

export const DEFAULT_RUN_PROJECT_API_RATE_LIMIT_CONFIG: RunProjectApiRateLimitConfig =
  {
    hourRateLimit: 60,
    minuteRateLimit: 20,
  };

export interface ClientDevtoolsFlagConfig {
  app_session_diff_checker: boolean;
  cell_debugger: boolean;
  chart_debugger: boolean;
  explore_debugger: boolean;
  external_profiler_user: boolean;
  hisc_debugger: boolean;
  internal_devtools_enabled: boolean;
  magic_debugger: boolean;
  multiplayer_debugger: boolean;
  python_error_reader: boolean;
  something_else: boolean;
  threads_debugger: boolean;
}

const DEFAULT_CLIENT_DEVTOOLS_FLAG_CONFIG: ClientDevtoolsFlagConfig = {
  app_session_diff_checker: false,
  cell_debugger: false,
  chart_debugger: false,
  explore_debugger: false,
  external_profiler_user: false,
  hisc_debugger: false,
  internal_devtools_enabled: false,
  magic_debugger: false,
  multiplayer_debugger: false,
  python_error_reader: false,
  something_else: false,
  threads_debugger: false,
};

export type BackfillQueryConfig = {
  queries: {
    batch_count: string;
    table: string;
  }[];
};

export const DEFAULT_BACKFILL_CONFIG = {
  queries: [],
};

export type WebsocketAuthCachingConfig = {
  timeout: number | null;
};

export type SemanticCapsPerEntityConfig = {
  [Entity in PermissionedEntityTypeName | "ENABLE_ALL_ENTITIES"]?: boolean;
};

// This LD flag represents whether or not we should use SemanticCaps auth checks for the entity type.
// The default config is an empty dictionary, meaning all SemanticCaps are disabled for each entity.
export const DEFAULT_SEMANTIC_CAPS_PER_ENTITY_CONFIG: SemanticCapsPerEntityConfig =
  {};

export type DataSourceSchemaSleeperConfig = {
  sleepEvery: number;
  sleepForMs: number;
};

export type DwhTableSearchConfig = {
  fts: {
    keywordBoost: number;
  };
  rerank: {
    rrf: {
      c: number;
    };
    weights: {
      endorsedBoost: number;
      fts: number;
      semantic: number;
    };
  };
};

export type AuditLogWorkOSBackfillConfig = {
  config?: {
    dry_run: boolean;
    end_time: number;
    event_type_filter?: string[];
    orgIds?: string[];
    start_time: number;
  };
  enabled: boolean;
};

export type SchemaRefreshRateLimitConfig = {
  enabled: boolean;
  globalLimit: number;
  orgLimit: number;
};

export type SchemaRefreshInMemoryRateLimitConfig = RateLimitConfig & {
  batchSize: number;
};

const DEFAULT_SCHEMA_REFRESH_RATE_LIMIT_CONFIG: SchemaRefreshRateLimitConfig = {
  enabled: false,
  globalLimit: 10,
  orgLimit: 1,
};

export type ReserveKernelsForInteractiveConfig = {
  maxReservations: number;
  poolReservationRatio: number;
};

export const DEFAULT_RESERVE_KERNELS_FOR_INTERACTIVE_CONFIG: ReserveKernelsForInteractiveConfig =
  {
    maxReservations: 30,
    poolReservationRatio: 0.2,
  };

export const CustomGraphqlObjectAuthzCheckType = {
  LOG_ONLY: "log_only",
  REQUIRE: "require",
  SKIP: "skip",
};
export type CustomGraphqlObjectAuthzCheckType =
  (typeof CustomGraphqlObjectAuthzCheckType)[keyof typeof CustomGraphqlObjectAuthzCheckType];

export type DisableSingleGraphqlObjectAuthz = {
  [key: string]: "DISABLE";
};

export type DisableProjectFiltersByType = {
  [key: string]: "DISABLE" | "ENABLE";
};

export type SchemaRefreshInterruptType =
  | "NO_INTERRUPT"
  | "PAUSE"
  | "DISRUPTIVE_CANCEL";

export const IdleKernelExpirySettings = R.Record({
  cronSchedule: R.Union(R.String, R.Undefined),
  gracePeriodMs: R.Union(R.Number, R.Undefined),
  limit: R.Union(R.Number, R.Undefined),
});

export type IdleKernelExpirySettings = R.Static<
  typeof IdleKernelExpirySettings
>;

export const defaultIdleKernelExpirySettings = {
  cronSchedule: "*/1 * * * *",
  gracePeriodMs: 30 * 1000,
  limit: 100,
} satisfies IdleKernelExpirySettings;

export const LanceSyncQueueLimiterSettings = R.Record({
  duration: R.Number,
  max: R.Number,
});

export type LanceSyncQueueLimiterSettings = R.Static<
  typeof LanceSyncQueueLimiterSettings
>;

export const defaultLanceSyncQueueLimiterSettings = {
  duration: 10_000, // Lance timeout is 10 seconds
  max: 10, // Our minimum number of pods is 10
} satisfies LanceSyncQueueLimiterSettings;

export const McpGetThreadToolConfig = R.Record({
  maxThreadStatusChecks: R.Number,
  threadStatusCheckIntervalMs: R.Number,
});
export type McpGetThreadToolConfig = R.Static<typeof McpGetThreadToolConfig>;

export interface PublicApiBanConfig {
  isBanned: boolean;
}

const ReduceDefaultLogicIdleTimeoutFlag = R.Record({
  reduceDefaultTimeoutAfter: StringDate,
});

export type ReduceDefaultLogicIdleTimeoutFlag = R.Static<
  typeof ReduceDefaultLogicIdleTimeoutFlag
>;

export function parseReduceDefaultLogicIdleTimeoutFlag(
  obj: Record<string, unknown>,
): ReduceDefaultLogicIdleTimeoutFlag | null {
  try {
    return ReduceDefaultLogicIdleTimeoutFlag.check(obj);
  } catch (_e) {
    return null;
  }
}

export const SemanticIngestEsRateLimitConfig = RateLimitConfig;
export type SemanticIngestEsRateLimitConfig = RateLimitConfig;

export const RunProjectApiRateLimitConfig = R.Record({
  hourRateLimit: R.Number,
  minuteRateLimit: R.Number,
});

export type RunProjectApiRateLimitConfig = R.Static<
  typeof RunProjectApiRateLimitConfig
>;

export const AppSessionGarbageCollectorSettings = R.Record({
  cellLimit: R.Number,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  outputLimit: R.Number,
});

export type AppSessionGarbageCollectorSettings = R.Static<
  typeof AppSessionGarbageCollectorSettings
>;

export const defaultAppSessionGarbageCollectorSettings: AppSessionGarbageCollectorSettings =
  {
    cellLimit: 10_000,
    cron: "*/15 * * * *", // every 15 minutes
    disabled: false,
    limit: 1_000,
    outputLimit: 10_000,
  };

export const ComputeActionGarbageCollectorSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  maxAgeInSeconds: R.Number,
});

export type ComputeActionGarbageCollectorSettings = R.Static<
  typeof ComputeActionGarbageCollectorSettings
>;

export const defaultComputeActionGarbageCollectorSettings: ComputeActionGarbageCollectorSettings =
  {
    // Every 5 minutes starting at 3 minutes past the hour
    cron: "3-59/5 * * * *",
    // We require this flagged on in LD
    disabled: false,
    // 5000 every 5 minutes is roughly the rate prod produces events in Jan 2025
    limit: 5000,
    // Our current DataDog log retention is 3 days so hold onto them for 3 days is reasonable
    maxAgeInSeconds: 7 * 24 * 60 * 60,
  };

export const OrphanedFileDeletionJobSettings = R.Record({
  createdCutoff: StringDate,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  iterations: R.Number,
});
export type OrphanedFileDeletionJobSettings = R.Static<
  typeof OrphanedFileDeletionJobSettings
>;

export const defaultOrphanedFileDeletionJobSettings: OrphanedFileDeletionJobSettings =
  {
    createdCutoff: StringDate.check("2025-09-19T00:00:00.000Z"), // only consider objects created before this cutoff
    cron: "*/10 * * * *", // every 10 minutes
    disabled: false,
    iterations: 10, // number of pages of files to check per job (page size limited to 1000 by S3)
  };

export const AsyncifySyncMethodConfig = R.Record({
  batchDelayMs: R.Number.optional(),
  enabled: R.Boolean,
  maxBatchTimeMs: R.Number.optional(),
});
export type AsyncifySyncMethodConfig = R.Static<
  typeof AsyncifySyncMethodConfig
>;

export const MagicNotebookLogicViewConfig = R.Record({
  enabled: R.Boolean,
  includeExperimentalTools: R.Boolean,
  maxAgentIterations: R.Number,
});
export type MagicNotebookLogicViewConfig = R.Static<
  typeof MagicNotebookLogicViewConfig
>;
export const defaultMagicNotebookLogicViewConfig: MagicNotebookLogicViewConfig =
  {
    enabled: false,
    includeExperimentalTools: false,
    maxAgentIterations: 0,
  };

export const ChatWithAppConfig = R.Record({
  enabled: R.Boolean,
});
export type ChatWithAppConfig = R.Static<typeof ChatWithAppConfig>;
export const defaultChatWithAppConfig: ChatWithAppConfig = {
  enabled: false,
};

export const TemporalWorkerConcurrencyConfig = R.Record({
  maxConcurrentActivityTaskExecutions: R.Number,
  maxConcurrentWorkflowTaskExecutions: R.Number,
});
export type TemporalWorkerConcurrencyConfig = R.Static<
  typeof TemporalWorkerConcurrencyConfig
>;
export const defaultTemporalWorkerConcurrencyConfig: TemporalWorkerConcurrencyConfig =
  {
    maxConcurrentActivityTaskExecutions: 75,
    maxConcurrentWorkflowTaskExecutions: 50,
  };

// name all the tool strategies here
export const MagicExploreToolStategy = R.Union(
  R.Literal("plan-exec"),
  R.Literal("fine-grained-tools"),
  R.Literal("set-chart-set-pivot"),
);
export type MagicExploreToolStategy = R.Static<typeof MagicExploreToolStategy>;

export const MagicExploreChatConfig = R.Record({
  delayMs: R.Number.optional(), // The delay time between polls to override
  enabled: R.Boolean, // whether the agent is enabled for Explore
  maxAgentIterations: R.Number, // Maxinum number of agent iterations allowed
  maxRetries: R.Number.optional(), // The number of times to poll/retry the agent
  parallel: R.Boolean.optional(), // Enable parallel tool calling
  toolStrategy: MagicExploreToolStategy, // The tool strategy to use
});
export type MagicExploreChatConfig = R.Static<typeof MagicExploreChatConfig>;
export const defaultMagicExploreChatConfig: MagicExploreChatConfig = {
  enabled: false,
  maxAgentIterations: 0,
  parallel: true,
  toolStrategy: "plan-exec",
};

export type TypeaheadV2Config = {
  enabledPython: boolean;
  enabledSql: boolean;
  modelKeys: { dev: string; eu: string; us: string };
};

// Only used for AgentAskSemanticModelsSearch right now, but HybridIndex is standalone.
// This could be used by other HybridIndex-based features later.
export const HybridIndexConfig = R.Record({
  // Controls the baseten model used for computing dense vector embeddings.
  denseEmbeddingModelKey: R.String,
});
export type HybridIndexConfig = R.Static<typeof HybridIndexConfig>;
export const defaultHybridIndexConfig: HybridIndexConfig = {
  denseEmbeddingModelKey: "UNSET_MODEL_KEY",
};

export const AgentAskSemanticModelsSearch = R.Record({
  enableRedisCaching: R.Boolean,
  filterToEndorsedOnly: R.Boolean,
});
export type AgentAskSemanticModelsSearch = R.Static<
  typeof AgentAskSemanticModelsSearch
>;
export const defaultAgentAskSemanticModelsSearch: AgentAskSemanticModelsSearch =
  {
    enableRedisCaching: false,
    filterToEndorsedOnly: false,
  };

export const StripOutputContentsConfig = R.Record({
  debounceTimeoutMs: R.Number,
  inlineCutoffBytes: R.Number,
  maxWaitMs: R.Number,
  stripContents: R.Boolean,
});
export type StripOutputContentsConfig = R.Static<
  typeof StripOutputContentsConfig
>;
export const DEFAULT_STRIP_OUTPUT_CONTENTS_CONFIG: StripOutputContentsConfig = {
  debounceTimeoutMs: 500,
  inlineCutoffBytes: CONSIDER_LAZY_LOADING_SIZE,
  maxWaitMs: 2000,
  stripContents: false,
};

export const AppSessionEventBatchingConfig = R.Record({
  debounceTimeout: R.Number,
  maxBatchSize: R.Number,
  maxWait: R.Number,
});
export type AppSessionEventBatchingConfig = R.Static<
  typeof AppSessionEventBatchingConfig
>;
export const DEFAULT_APP_SESSION_BATCHING_CONFIG: AppSessionEventBatchingConfig =
  {
    debounceTimeout: 10,
    maxBatchSize: 25,
    maxWait: 100,
  };

export const AppSessionEventsConfig = R.Partial({
  apiSessions: R.Boolean,
  defaultSessions: R.Boolean,
  logicSessions: R.Boolean,
  publishedSessions: R.Boolean,
  scheduledSessions: R.Boolean,
});
export type AppSessionEventsConfig = R.Static<typeof AppSessionEventsConfig>;
export const DEFAULT_APP_SESSION_EVENTS_CONFIG: Required<AppSessionEventsConfig> =
  {
    apiSessions: false,
    defaultSessions: false,
    logicSessions: false,
    publishedSessions: false,
    scheduledSessions: false,
  };

export const ModelProviderConfig = R.Record({
  batchSize: R.Number,
  concurrency: R.Number,
});
export type ModelProviderConfig = R.Static<typeof ModelProviderConfig>;

export const HexVersionAutosaveGarbageCollectorSettings = R.Record({
  appSessionBatchSize: R.Number,
  batchSize: R.Number,
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
  shouldLogAutosaveIds: R.Boolean,
});

export type HexVersionAutosaveGarbageCollectorSettings = R.Static<
  typeof HexVersionAutosaveGarbageCollectorSettings
>;

export const defaultHexVersionAutosaveGarbageCollectorSettings: HexVersionAutosaveGarbageCollectorSettings =
  {
    appSessionBatchSize: 100,
    batchSize: 20,
    cron: "10 * * * *",
    disabled: false,
    limit: 100,
    shouldLogAutosaveIds: false,
  };

export const SupportUserSharingCleanupSettings = R.Record({
  cron: R.String.withConstraint(isValidCron),
  disabled: R.Boolean,
  limit: R.Number,
});

export type SupportUserSharingCleanupSettings = R.Static<
  typeof SupportUserSharingCleanupSettings
>;

export const defaultSupportUserSharingCleanupSettings: SupportUserSharingCleanupSettings =
  {
    cron: "30 3 * * *", // Daily at 3:30 AM UTC
    disabled: false,
    limit: 100,
  };

export interface FeatureFlagSet extends FlaggedNewUserFeatureSet {
  "additional-mariadb-params": boolean;
  "agent-ask-semantic-models-search": AgentAskSemanticModelsSearch;
  "agent-claude-native-memory": boolean;
  "agent-parallel-tool-calls": AgentParallelToolCallsConfig;
  "agent-sees-semantic-column-sql-expression": boolean;
  "aggregate-ai-usage-on-creation": boolean;
  "allow-duplication-for-project": boolean;
  "allow-embedded-user": boolean;
  "allow-public-domain-signups": boolean;
  "anthropic-context-editing-config": AnthropicContextEditingConfig;
  "ao-rate-limit": Record<string, unknown>;
  // experiment flag to be configured at 50% by app session
  "app-session-a-a": boolean;
  "app-session-event-batching-config": unknown;
  "app-session-events-config": unknown;
  "app-session-garbage-collector-settings": unknown; // expected to be Partial<AppSessionGarbageCollectorSettings>
  "app-session-mp-client-logging": boolean;
  "app-state-hydration": boolean;
  "app-state-persistence": boolean;
  "ask-agent-slack-bot": boolean;
  "asyncify-prompts-for-project-deflection": AsyncifySyncMethodConfig;
  "audit-log-workos-backfill": AuditLogWorkOSBackfillConfig;
  "audit-log-workos-backfill-interrupt": boolean;
  "auto-rebuild-custom-images-delay": number;
  "auto-rebuild-docker-images": boolean;
  "auto-rebuild-docker-images-cron-job": boolean;
  "auto-refresh-client-config": Record<string, unknown>;
  "auto-reload-client-on-auth-fail": boolean;
  "auto-rerun-app-period-seconds": number;
  "backfill-data-connection-auth-type": boolean;
  "base-kernel-image-gate": {
    defaultPythonKernelImage: string;
    enabled: string[];
    python39NullCutoffDate: string;
  };
  "baseten-embedding-model-key": string;
  "bigquery-additional-project-ids": boolean;
  "block-unsupported-type-conversions": boolean;
  "bulk-deactivate-users-by-domain": boolean;
  "bull-queue-job-timeout-override": Record<string, unknown>; // expected Record<BullQueueId, number>
  "bull-queue-repair-maxed-groups": boolean;
  "bull-queue-slow-job-threshold-override": Record<string, unknown>;
  "byok-cortex-enabled": boolean;
  "byok-enabled-override": boolean;
  "caching-ux-app-publish-redesign": boolean;
  "caching-ux-results-age-indicator-redesign": boolean;
  "caching-ux-reuse-app-session": boolean;
  "cast-string-columns-for-filters": boolean;
  "cell-compilation-timeout-ms": number;
  "cell-debugger": boolean;
  "cell-references-fix-migration": boolean;
  "chain-sql-engine": "legacy" | "sqlparse" | "sqlglot";
  "chat-with-app-config": ChatWithAppConfig;
  "check-kernel-limits": boolean;
  "clickhouse-json-column": boolean;
  "client-devtools": ClientDevtoolsFlagConfig;
  "compute-action-garbage-collector-settings": Partial<ComputeActionGarbageCollectorSettings>;
  "compute-heartbeat-send-to-segment-enabled": boolean;
  "compute-heartbeat-sync-to-stripe-cron-schedule": string;
  "compute-heartbeat-sync-to-stripe-mode": string;
  "compute-heartbeat-sync-to-stripe-status": ComputeHeartbeatSyncToStripeStatus;
  "connections-per-trace-debounce-ms": number;
  "connections-per-trace-warning-threshold": number;
  "consolidated-kernel-initialization": boolean;
  "cp-diff-tracking-cron-schedule": string;
  "cp-diff-tracking-delay-threshold-ms": number;
  "cp-diff-tracking-enabled": boolean;
  "cp-kernel-management": KernelManagementCPMigrationState;
  "cryptominer-regex": string;
  "custom-kernels-new-auto-rebuild-logic": boolean;
  "custom-kernels-ui": boolean;
  "custom-kernels-ui-v2": boolean;
  "data-conn-diff-schema-update": boolean;
  "data-conn-diff-schema-update-threshold": number;
  "data-conn-insert-chunk-size": number;
  "data-conn-sleep-times": DataSourceSchemaSleeperConfig;
  "data-conn-tree-schema": boolean;
  "data-connection-databricks-proxy": boolean;
  "data-connection-deletion-job-limit": number;
  "data-connection-motherduck-enable-read-scaling": boolean;
  "data-connection-snowflake-async-queries": boolean;
  "data-connection-status-details-killswitch": boolean;
  "data-connection-types-to-limit": Partial<
    Record<DataConnectionType | "ssh", number>
  >;
  "data-connections-header-cleanup": boolean;
  "data-recommended-tab": boolean;
  "data-retention-available": boolean;
  "data-retention-enable-outputs": boolean;
  "data-retention-enable-query-cache": boolean;
  "data-service-dfs-batch-size": number;
  "data-service-dfs-max-bytes-per-batch": number;
  "data-service-table-load-batch-size": number;
  "database-backfill-cron": string;
  "database-backfill-queries": BackfillQueryConfig;
  "database-query-permissions": boolean;
  "databricks-jdbc-upgrade": boolean;
  "dbt-data-conn-sleep-times": DataSourceSchemaSleeperConfig;
  "dbt-disallow-proxy-server": boolean;
  "dbt-fetch-batch-size": number;
  "dbt-timeout-metadata": number;
  "dc-schema-filter-ui": boolean;
  "dd-retain-execution-trace": boolean;
  "deadlock-detector-timeout-s": number;
  "debug-gh-sync": boolean;
  "default-display-table": boolean;
  "delete-app-session-api-client-on-kill": boolean;
  "dfs-schema-traversal": boolean;
  "disable-agent-chat-garbage-collector": boolean;
  "disable-ao-garbage-collector": boolean;
  "disable-bull-queue-job-timeout": boolean;
  "disable-chat-support-for-company-closure": {
    available: boolean;
    cta: string;
    message: string;
  };
  "disable-dereference-agent-client-to-user": boolean;
  "disable-embedded-user-api-client-garbage-collector": boolean;
  "disable-execution-event-writes": boolean;
  "disable-hex-garbage-collector": boolean;
  "disable-hex-version-default-app-session-garbage-collector": boolean;
  "disable-large-pivot-csv": boolean;
  "disable-presigned-embed-url-garbage-collector": boolean;
  "disable-publish-column-profiling": boolean;
  "disable-workspace-sharing": boolean;
  "do-not-charge-for-kernels-created-by-hex": boolean;
  "duckdb-query-arrow-datasets": boolean;
  "dwh-table-search-config": DwhTableSearchConfig;
  "embeddings-baseten-model-keys": { [K in BasetenEmbeddingModel]?: string };
  "embeddings-garbage-collection": boolean;
  "embeddings-garbage-collection-deletion-batch-size": number;
  "embeddings-garbage-collection-search-batch-size": number;
  "embeddings-mpk-config": {
    [keyProvider in LanceEmbeddingProvider]?: {
      [keyModel in LanceEmbeddingModel]?: ModelProviderConfig;
    };
  };
  "embeddings-orphan-deletion": boolean;
  "embeddings-orphan-deletion-batch-size": number;
  "embeddings-orphan-search-minimum-batch-size": number;
  "embeddings-project-retrieval-mpk": EmbeddingModelProviderKeyType;
  "embeddings-table-retrieval-mpk": EmbeddingModelProviderKeyType;
  "emit-dbt-data-size-metric": boolean;
  "emit-schema-size-metric": boolean;
  "enable-api-enhancements": boolean;
  "enable-cross-request-semantic-cap-cache": boolean;
  "enable-delete-pods-from-wiz-issues": boolean;
  "enable-embedding-app-freshness-options": boolean;
  "enable-hybrid-search-find-table-schemas": boolean;
  "enable-iam-role-based-ecr-repo-authentication": boolean;
  "enable-monitor-replication-slots": boolean;
  "enable-oauth-schema-auth": boolean;
  "enable-parquet-cache-mode": boolean;
  "enable-reindex-schema": boolean;
  "enable-rerank-search-users": boolean;
  "enable-run-project-with-trace": boolean;
  "enable-schema-refresh-download-grants": boolean;
  "enable-sidebar-docking": boolean;
  "enable-snowflake-oauth-sso": boolean;
  "enable-sql-completion-logs": boolean;
  "enforce-compute-spend-limits": boolean;
  "es-bulk-delete-chunk-size": number;
  "es-bulk-timeout-schema-refresh": string;
  "es-delete-all-datasources-delay-ms": number;
  "es-delete-by-query-timeout": string;
  "es-index-config": ElasticsearchIndexConfig;
  "es-index-reader-config": ElasticsearchIndexReaderConfig;
  "es-reindex-config": ElasticsearchReindexConfig;
  "es-sync-batch-size": number;
  "es-sync-hex-chunk-size": number;
  "es-sync-hex-job-limit": number;
  "experimental-send-rengagement-email": boolean;
  "experimentation-test": boolean;
  "explorable-apps": boolean;
  "explore-app-cell-view-data": boolean;
  "explore-backed-charts": boolean;
  "explore-backed-pivots": boolean;
  "explore-cancel-button": boolean;
  "explore-debug-outputs": boolean;
  "explore-filter-measures": boolean;
  // If enabled, Explorer role is visible and can be assigned to users.
  // Deprecated: Use Org.explorerRoleEnabled instead.
  "explorer-role": boolean;
  "fast-forward-implementation": unknown;
  "filter-non-endorsed-tables-for-magic-explore": boolean;
  "force-live-updates-for-flags": FeatureFlag[] | ["*"];
  "force-redefinition-edges": boolean;
  "fstr-timeout-ms": number;
  "garbage-collection-rate-limit-config": GarbageCollectionRateLimitConfig;
  "generate-suggested-prompts-sync-enabled": boolean;
  "git-package-import-v2": boolean;
  "google-cloud-storage-enabled": boolean;
  // TODO we can coalesce grant-access-for-project-from-slack with slack-interactivity
  "grant-access-for-project-from-slack": boolean;
  "graphview-update": boolean;
  "hashed-bearer-tokens": boolean;
  "hex-garbage-collection-app-session-cell-limit": number;
  "hex-garbage-collection-hex-version-limit": number;
  "hex-to-es-sync-mode": HexToESSyncMode;
  "hex-version-autosave-gc-settings": unknown; // expected Partial<HexVersionAutosaveGarbageCollectorSettings>
  "hisc-explore-cell": boolean;
  "hourly-schedule-limit": number;
  "human-titles-in-url": boolean;
  "hybrid-index-config": HybridIndexConfig;
  "hybrid-vega-renderer": boolean;
  "hydrate-explore-cell": boolean;
  "idle-kernel-expiry-settings": IdleKernelExpirySettings;
  "in-app-announcements": Record<string, unknown>[];
  "in-app-releases": Record<string, unknown>;
  "in-app-whats-new": Record<string, unknown>;
  "indirect-query-event-aggregation": boolean;
  "input-cell-formatting": boolean;
  "intermediate-state-caching": boolean;
  "internal-test-collection-job-delay-ms": number;
  "internal-test-collection-queue": InternalTestCollectionQueue;
  // Install `transform` python package after upgrading to Pandas 1.5
  "kernel-lifetime-stats": boolean;
  "kernel-metric-logger": boolean;
  "kernel-metric-logger-code": boolean;
  "lance_db-sync-limiter-settings": Record<string, unknown>;
  "lance-db-admin-only-backfill-ui": boolean;
  "lance-db-data-conn-backfill-data-conn-to-skip": string[];
  "lance-db-data-conn-backfill-frequency-ms": number;
  "lance-db-data-conn-backfill-limit": number;
  "lance-db-data-conn-backfill-retry-limit": number;
  "lance-db-data-conn-errored-actions-limit": number;
  "lance-db-data-conn-stuck-in-backfilling-limit": number;
  "lance-db-entity-promotion-limit": number;
  "lance-db-hex-backfill": boolean;
  "lance-db-hex-backfill-excluded-orgs": string[];
  "lance-db-hex-backfill-frequency-ms": number;
  "lance-db-hex-backfill-limit": number;
  "lance-db-hex-errored-actions-limit": number;
  "lance-db-hex-stuck-in-backfilling-limit": number;
  "lance-db-hex-stuck-in-processing-limit": number;
  "lance-db-long-running-action-threshold-hours": number;
  "lance-db-sync-batch-lance-actions-queue-interval-ms": number;
  "lance-db-sync-batch-size": number;
  "lance-db-table-retrieval-limit": number;
  "lance-db-table-retrieval-minimum-result-size": number;
  "lance-db-table-retrieval-timeout-ms": number;
  "lance-db-un-indexed-rows-threshold": number;
  "lance-db-use-actions-v2": boolean;
  "lance-db-where-batch-size": number;
  "lance-db-write-timeout-ms": number;
  "lazy-load-results": boolean;
  "lazy-load-results-bq": boolean;
  "legacy-git-sync": boolean;
  "log-dbt-metadata-counts": boolean;
  "logic-session-timeout-extension": boolean;
  "magic-deflection": boolean;
  "magic-deflection-cell-limit": number;
  "magic-deflection-embed": boolean;
  "magic-esignature": boolean;
  "magic-example-table-prompts": number;
  "magic-example-table-prompts-query-threshold": number;
  "magic-explore": boolean;
  "magic-explore-chat-config": MagicExploreChatConfig;
  "magic-explore-edit": boolean;
  "magic-explore-edit-logic-view": boolean;
  "magic-explore-guess-primary-key-limit": number;
  "magic-explore-planexec-retry-count": number;
  "magic-lance-project-retrieval-limit": number;
  "magic-lance-project-retrieval-vector-distance-threshold": number;
  "magic-max-other-dfs": number;
  "magic-notebook-duplicate-legacy-vis-tools": boolean;
  "magic-notebook-logic-view-config": MagicNotebookLogicViewConfig;
  "magic-notebook-logic-view-with-temporal": boolean;
  "magic-slack-bot": boolean;
  "magic-smoke-test-single-cell": boolean;
  "magic-suggested-endorsements": boolean;
  "magic-suggested-prompts-pd-events-limit": number;
  "magic-suggested-prompts-pd-limit": number;
  "magic-summary": boolean;
  "magic-table-formatting-method": MagicTableFormattingMethod;
  "magic-table-retrieval-columns-per-table-limit": number;
  "magic-table-retrieval-desc-similarity-threshold": number;
  "manual-work-os-syncs": boolean;
  "map-us-county-data": boolean;
  "mark-orgs-from-wiz-issues": boolean;
  "max-attempts-wait-for-es-task-completion": number;
  "max-dynamic-list-size": number;
  "max-fstr-results": number;
  "max-kernels-per-org": number;
  "max-kernels-per-user": number;
  "max-semantic-datasets-for-dataset-groups-component": number;
  "max-signup-limit": number;
  "max-untrusted-kernels": number;
  "maybe-embed-hex-delay-ms": number;
  "mcp-get-thread-tool-config": McpGetThreadToolConfig;
  "mcp-server": boolean;
  "minerva-circuit-breaker-enabled": boolean;
  "minerva-epoch": number;
  /**
   * A dictionary of model feature keys to their configuration.
   * It should be type guarded against ModelFeatureConfig before being used.
   */
  "model-feature-config": unknown;
  "new-onboarding-experience-threads": boolean;
  "new-seat-onboarding-experience": boolean;
  "no-max-signups": boolean;
  "no-op-garbage-collector-queues": boolean;
  "oauth-collab-always-expose-outputs": boolean;
  "oauth-dc-service-account-schema": boolean;
  "oauth-schemas-cache-enabled": boolean;
  "online-thread-eval-ui": boolean;
  "online-thread-evals": boolean;
  "only-add-code-cells-on-first-run": boolean;
  "open-ai-rate-limit-config": OpenAiRateLimitConfig;
  "optimize-explore-hql-date-filters": boolean;
  "orphaned-file-deletion-job-settings": unknown;
  "output-content-read-dynamo": boolean;
  "output-content-write-dynamo": boolean;
  "pandas-copy-on-write": boolean;
  "password-users": boolean;
  "pip-shim-suggest-uv": boolean;
  "prewarm-kernels-ux-enablement": boolean;
  "priority-cell-execution": boolean;
  "product-launch": boolean;
  "product-launch-demo-hide-beta-flags": boolean;
  "project-chrome-v-6": boolean;
  "prompt-injection-window-size": number;
  "public-api-ban-config": PublicApiBanConfig;
  "pylon-chat": boolean;
  "query-event-queue-enabled": boolean;
  "query-mode-default-on": boolean;
  "quick-search-query-config": QueryConfig;
  "redis-reuse-connections": boolean;
  "reduce-default-logic-idle-timeout-datetime": Record<string, unknown>;
  "register-ready-pods": boolean;
  "reindex-es-batch-size": number;
  "reindex-es-job-limit": number;
  "reindex-schema-rate-limit-duration": number;
  "reindex-schema-rate-limit-points": number;
  "remote-dataframes-deprecated": boolean;
  "reserve-kernels-for-interactive": ReserveKernelsForInteractiveConfig;
  "resource-sync-to-compute-heartbeat-enabled": boolean;
  "run-project-api-rate-limit-config": RunProjectApiRateLimitConfig;
  "run-selected-sql": boolean;
  "scheduled-run-concurrency": number;
  "scheduled-run-delay": number;
  "scheduled-run-stop-button": boolean;
  "schema-chunk-size": number;
  "schema-refresh-data-service-batch-size": number;
  "schema-refresh-dfs-download-finds-orphans": boolean;
  "schema-refresh-disable-gc": boolean;
  "schema-refresh-download-grants-rate-limit-config": SchemaRefreshInMemoryRateLimitConfig;
  "schema-refresh-download-schema-batch-size": number;
  "schema-refresh-gc-batch-size": number;
  "schema-refresh-gc-delay-ms": number;
  "schema-refresh-get-orphaned-entities-batch-size": number;
  "schema-refresh-get-schema-batch-size": number;
  "schema-refresh-interrupt": SchemaRefreshInterruptType;
  "schema-refresh-max-column-count": number;
  "schema-refresh-rate-limit-duration": number;
  "schema-refresh-rate-limit-points": number;
  "schema-refresh-rate-limit-points-dfs": number;
  "schema-refresh-update-db-batch-size": number;
  "schema-refresh-update-es-rate-limiter": SchemaRefreshInMemoryRateLimitConfig;
  "schema-refresh-update-es-table-column-batch-size": number;
  "schema-refresh-update-lance-batch-size": number;
  "schema-refresh-use-get-datasource-entity-endpoints": boolean;
  "schema-refresh-v3-rate-limiting": SchemaRefreshRateLimitConfig;
  "screenshot-extra-timeout": number;
  "screenshot-max-height": number;
  "screenshot-max-retries": number;
  "search-workspace-rules-tool": boolean;
  "semantic-authoring-agent-with-temporal": boolean;
  "semantic-authoring-history": boolean;
  "semantic-ingest-es-rate-limit-duration": SemanticIngestEsRateLimitConfig;
  "semantic-sql-tool": boolean;
  "semantic-views": boolean;
  "semantic-workbench-typeahead": boolean;
  "sidecar-batch-debounce-ms": number;
  "sidecar-execution-events": boolean;
  "sidecar-health-monitoring-interval": number;
  "sidecar-mp-concurrency-limit": number;
  "sidecar-output-batch-debounce-ms": number;
  "signed-embedding-api-rate-limit": number;
  "skip-mp-ops-on-noninteractive-runs": boolean;
  "skip-old-sidecar-requests": boolean;
  "slack-channel-query-limit": number;
  "slack-files-read": boolean;
  "slack-integration-v2": boolean;
  "slack-interactivity": boolean;
  "smoke-test-cleanup-cron": string;
  "smoke-test-cleanup-limit": number;
  "sql-cell-query-mode-async": boolean;
  "st-prewarmed-defaults": PrewarmedDefaultInfo;
  "store-embedded-api-calls": boolean;
  "stream-motherduck-results": boolean;
  "stream-schema-grant-data": boolean;
  "strip-output-contents-redux": unknown;
  "stripe-check-events-minimum-ms": number;
  "supernova-a-a": boolean;
  "support-user-sharing-cleanup-settings": unknown; // expected Partial<SupportUserSharingCleanupSettings>
  "table-deletion-queue-batch-size": number; // setting this to 0 will disable the queue
  "table-deletion-queue-cron": string;
  "table-store-hex-versioning-enabled": boolean;
  "temporal-worker-concurrency-config": TemporalWorkerConcurrencyConfig;
  "thread-inspector-sidebar": boolean;
  "threads-copy-download-viz": boolean;
  "threads-data-sources": boolean;
  "threads-kernel-idle-timeout-ms": number | null;
  "threads-mention-semantic-models": boolean;
  "threads-observability-page": boolean;
  "threads-question-router": boolean;
  "threads-view-project-sql": boolean;
  "threads-with-temporal": boolean;
  "trino-session-user-passthrough": boolean;
  "typeahead-v-2": TypeaheadV2Config;
  "use-create-database-v2": boolean;
  "use-custom-kernels-only": boolean;
  "use-in-house-ck": boolean;
  "use-only-register-ready-pods": boolean;
  "use-replica-db-if-available": boolean;
  "user-rules-notebook-agent": boolean;
  "usercheck-domain-denial": boolean;
  "websocket-auth-caching": WebsocketAuthCachingConfig;
  "websocket-rate-limits": WebsocketRateLimitConfig;
  "workspace-invite-links": boolean;
  "workspace-search-enable-category-status-boost": boolean;
}

export type FeatureFlag = keyof FeatureFlagSet;

export const DEFAULT_FLAGS: FeatureFlagSet = {
  "additional-mariadb-params": false,
  "agent-ask-semantic-models-search": defaultAgentAskSemanticModelsSearch,
  "agent-claude-native-memory": false,
  "agent-parallel-tool-calls": DEFAULT_AGENT_PARALLEL_TOOL_CALLS_CONFIG,
  "agent-sees-semantic-column-sql-expression": false,
  "aggregate-ai-usage-on-creation": false,
  "allow-duplication-for-project": false,
  "allow-embedded-user": false,
  "allow-public-domain-signups": false,
  "anthropic-context-editing-config": { enabled: false },
  "ao-rate-limit": DEFAULT_AO_RATE_LIMIT_CONFIG,
  "app-session-a-a": false,
  "app-session-event-batching-config": DEFAULT_APP_SESSION_BATCHING_CONFIG,
  "app-session-events-config": DEFAULT_APP_SESSION_EVENTS_CONFIG,
  "app-session-garbage-collector-settings":
    defaultAppSessionGarbageCollectorSettings,
  "app-session-mp-client-logging": false,
  "app-state-hydration": false,
  "app-state-persistence": false,
  "ask-agent-slack-bot": false,
  "asyncify-prompts-for-project-deflection": { enabled: false },
  "audit-log-workos-backfill": { enabled: false },
  "audit-log-workos-backfill-interrupt": false,
  "auto-rebuild-custom-images-delay": 250,
  "auto-rebuild-docker-images": false,
  "auto-rebuild-docker-images-cron-job": true,
  "auto-refresh-client-config": { enabled: false },
  "auto-reload-client-on-auth-fail": false,
  "auto-rerun-app-period-seconds": 0,
  "backfill-data-connection-auth-type": false,
  "base-kernel-image-gate": {
    defaultPythonKernelImage: "python39",
    enabled: ["python311", "python310", "python39"],
    python39NullCutoffDate: "2025-03-30T00:00:00.000Z",
  },
  "baseten-embedding-model-key": "openai_fallback",
  "bigquery-additional-project-ids": false,
  "block-unsupported-type-conversions": true,
  "bulk-deactivate-users-by-domain": false,
  "bull-queue-job-timeout-override": {},
  "bull-queue-repair-maxed-groups": false,
  "bull-queue-slow-job-threshold-override": {},
  "byok-cortex-enabled": false,
  "byok-enabled-override": false,
  "caching-ux-app-publish-redesign": false,
  "caching-ux-results-age-indicator-redesign": false,
  "caching-ux-reuse-app-session": false,
  "cast-string-columns-for-filters": true,
  "cell-compilation-timeout-ms": 60000,
  "cell-debugger": false,
  "cell-references-fix-migration": false,
  "chain-sql-engine": "legacy",
  "chat-with-app-config": defaultChatWithAppConfig,
  "check-kernel-limits": true,
  "clickhouse-json-column": false,
  "client-devtools": DEFAULT_CLIENT_DEVTOOLS_FLAG_CONFIG,
  "compute-action-garbage-collector-settings":
    defaultComputeActionGarbageCollectorSettings,
  "compute-heartbeat-send-to-segment-enabled": false,
  "compute-heartbeat-sync-to-stripe-cron-schedule": "*/5 * * * *", // default to every 5 minutes
  "compute-heartbeat-sync-to-stripe-mode": "test",
  "compute-heartbeat-sync-to-stripe-status": "JOB_DISABLED",
  "connections-per-trace-debounce-ms": 10_000,
  "connections-per-trace-warning-threshold": 10,
  "consolidated-kernel-initialization": false,
  "cp-diff-tracking-cron-schedule": "*/1 * * * *",
  "cp-diff-tracking-delay-threshold-ms": 100,
  "cp-diff-tracking-enabled": false,
  "cp-kernel-management": DefaultKernelManagementCPMigrationState,
  "cryptominer-regex": "",
  "custom-kernels-new-auto-rebuild-logic": false,
  "custom-kernels-ui": false,
  "custom-kernels-ui-v2": false,
  "data-conn-diff-schema-update": false,
  "data-conn-diff-schema-update-threshold": 0.1,
  "data-conn-insert-chunk-size": 1000,
  "data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "data-conn-tree-schema": false,
  "data-connection-databricks-proxy": false,
  "data-connection-deletion-job-limit": 10_000,
  "data-connection-motherduck-enable-read-scaling": true,
  "data-connection-snowflake-async-queries": false,
  "data-connection-status-details-killswitch": false,
  "data-connection-types-to-limit": {},
  "data-connections-header-cleanup": false,
  "data-recommended-tab": false,
  "data-retention-available": false,
  "data-retention-enable-outputs": false,
  "data-retention-enable-query-cache": false,
  "data-service-dfs-batch-size": 7500,
  "data-service-dfs-max-bytes-per-batch": 1024 * 1024 * 10, // 10MB
  "data-service-table-load-batch-size": 100,
  "database-backfill-cron": "",
  "database-backfill-queries": DEFAULT_BACKFILL_CONFIG,
  "database-query-permissions": false,
  "databricks-jdbc-upgrade": true,
  "dbt-data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "dbt-disallow-proxy-server": false,
  "dbt-fetch-batch-size": 500,
  "dbt-timeout-metadata": 5000,
  "dc-schema-filter-ui": false,
  "dd-retain-execution-trace": false,
  "deadlock-detector-timeout-s": 60,
  "debug-gh-sync": false,
  "default-display-table": false,
  "delete-app-session-api-client-on-kill": false,
  "dfs-schema-traversal": false,
  // Agent chat garbage collector is now implemented and ready for production
  "disable-agent-chat-garbage-collector": false,
  "disable-ao-garbage-collector": false,
  "disable-bull-queue-job-timeout": false,
  "disable-chat-support-for-company-closure": {
    available: true,
    cta: "",
    message: "",
  },
  "disable-dereference-agent-client-to-user": false,
  "disable-embedded-user-api-client-garbage-collector": false,
  "disable-execution-event-writes": false,
  "disable-hex-garbage-collector": false,
  "disable-hex-version-default-app-session-garbage-collector": false,
  "disable-large-pivot-csv": false,
  "disable-presigned-embed-url-garbage-collector": false,
  "disable-publish-column-profiling": false,
  "disable-workspace-sharing": false,
  // This is not the default on LD, but if we fail to pull this flag
  // we want to err on the side of _not_ accidentally charging customers.
  "do-not-charge-for-kernels-created-by-hex": true,
  "duckdb-query-arrow-datasets": false,
  "dwh-table-search-config": {
    fts: {
      keywordBoost: 1.3,
    },
    rerank: {
      rrf: {
        c: 60,
      },
      weights: {
        endorsedBoost: 2,
        fts: 0.4,
        semantic: 0.6,
      },
    },
  },
  "embeddings-baseten-model-keys": {
    "snowflake-arctic-embed-l-v2.0": "yqvypjgq", // dev
  },
  "embeddings-garbage-collection": false,
  "embeddings-garbage-collection-deletion-batch-size": 10,
  "embeddings-garbage-collection-search-batch-size": 100,
  "embeddings-mpk-config": {
    [LanceEmbeddingProvider.OPENAI]: {
      [OpenAIEmbeddingModel["text-embedding-ada-002"]]: {
        batchSize: 500,
        concurrency: 5,
      },
    },
  },
  "embeddings-orphan-deletion": false,
  "embeddings-orphan-deletion-batch-size": 10,
  "embeddings-orphan-search-minimum-batch-size": 100,
  "embeddings-project-retrieval-mpk": DEFAULT_MPK,
  "embeddings-table-retrieval-mpk": DEFAULT_MPK,
  "emit-dbt-data-size-metric": false,
  "emit-schema-size-metric": false,
  "enable-api-enhancements": false,
  "enable-cross-request-semantic-cap-cache": false,
  "enable-delete-pods-from-wiz-issues": false,
  "enable-embedding-app-freshness-options": false,
  "enable-hybrid-search-find-table-schemas": false,
  "enable-iam-role-based-ecr-repo-authentication": false,
  "enable-monitor-replication-slots": false,
  "enable-oauth-schema-auth": false,
  "enable-parquet-cache-mode": false,
  "enable-reindex-schema": false,
  "enable-rerank-search-users": false,
  "enable-run-project-with-trace": false,
  "enable-schema-refresh-download-grants": false,
  "enable-sidebar-docking": false,
  "enable-snowflake-oauth-sso": false,
  "enable-sql-completion-logs": false,
  "enforce-compute-spend-limits": false,
  "es-bulk-delete-chunk-size": 300,
  "es-bulk-timeout-schema-refresh": "1m",
  "es-delete-all-datasources-delay-ms": 10000,
  "es-delete-by-query-timeout": "1m",
  "es-index-config": {
    datasourceSchemas: {
      backfillIndexes: ["datasource_schemas-v0"],
      readerIndex: "datasource_schemas-v0",
      writerIndexes: ["datasource_schemas-v0"],
    },
    projects: {
      backfillIndexes: ["projects"],
      readerIndex: "projects",
      writerIndexes: ["projects"],
    },
  },
  "es-index-reader-config": {
    datasourceSchemas: "datasource_schemas-v0",
    projects: "projects",
  },
  "es-reindex-config": {
    initialIdOffset: {},
  },
  "es-sync-batch-size": 250,
  "es-sync-hex-chunk-size": 10,
  "es-sync-hex-job-limit": 100,
  "experimental-send-rengagement-email": false,
  "experimentation-test": false,
  "explorable-apps": false,
  "explore-app-cell-view-data": false,
  "explore-backed-charts": false,
  "explore-backed-pivots": true,
  "explore-cancel-button": false,
  "explore-debug-outputs": false,
  "explore-filter-measures": false,
  // Deprecated: Use Org.explorerRoleEnabled instead.
  "explorer-role": false,
  "fast-forward-implementation": DEFAULT_FAST_FORWARD_IMPLEMENTATION,
  "filter-non-endorsed-tables-for-magic-explore": false,
  "force-live-updates-for-flags": [],
  "force-redefinition-edges": false,
  "fstr-timeout-ms": 2000,
  "garbage-collection-rate-limit-config":
    DEFAULT_GARBAGE_COLLECTION_RATE_LIMIT_CONFIG,
  "generate-suggested-prompts-sync-enabled": false,
  "git-package-import-v2": false,
  "google-cloud-storage-enabled": false,
  "grant-access-for-project-from-slack": false,
  "graphview-update": false,
  "hashed-bearer-tokens": false,
  "hex-garbage-collection-app-session-cell-limit": 1000,
  "hex-garbage-collection-hex-version-limit": 100,
  "hex-to-es-sync-mode": HexToESSyncMode.DISABLED,
  "hex-version-autosave-gc-settings":
    defaultHexVersionAutosaveGarbageCollectorSettings,
  "hisc-explore-cell": false,
  "hourly-schedule-limit": 12,
  "human-titles-in-url": false,
  "hybrid-index-config": defaultHybridIndexConfig,
  "hybrid-vega-renderer": false,
  "hydrate-explore-cell": false,
  "idle-kernel-expiry-settings": defaultIdleKernelExpirySettings,
  "in-app-announcements": [],
  "in-app-releases": { on: false },
  "in-app-whats-new": { on: false },
  "indirect-query-event-aggregation": false,
  "input-cell-formatting": false,
  "intermediate-state-caching": false,
  "internal-test-collection-job-delay-ms": 5000,
  "internal-test-collection-queue": DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE,
  "kernel-lifetime-stats": false,
  "kernel-metric-logger": true,
  "kernel-metric-logger-code": true,
  "lance_db-sync-limiter-settings": defaultLanceSyncQueueLimiterSettings,
  "lance-db-admin-only-backfill-ui": false,
  "lance-db-data-conn-backfill-data-conn-to-skip": [],
  "lance-db-data-conn-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-data-conn-backfill-limit": 0,
  "lance-db-data-conn-backfill-retry-limit": 1,
  "lance-db-data-conn-errored-actions-limit": 0,
  "lance-db-data-conn-stuck-in-backfilling-limit": 10,
  "lance-db-entity-promotion-limit": 0,
  "lance-db-hex-backfill": false,
  "lance-db-hex-backfill-excluded-orgs": [],
  "lance-db-hex-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-hex-backfill-limit": 0,
  "lance-db-hex-errored-actions-limit": 0,
  "lance-db-hex-stuck-in-backfilling-limit": 0,
  "lance-db-hex-stuck-in-processing-limit": 0,
  "lance-db-long-running-action-threshold-hours": 12,
  "lance-db-sync-batch-lance-actions-queue-interval-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-sync-batch-size": 50000,
  "lance-db-table-retrieval-limit": 30,
  "lance-db-table-retrieval-minimum-result-size": 4,
  "lance-db-table-retrieval-timeout-ms": 5000,
  "lance-db-un-indexed-rows-threshold": 10_000,
  "lance-db-use-actions-v2": false,
  "lance-db-where-batch-size": 10000,
  "lance-db-write-timeout-ms": 10000,
  "lazy-load-results": false,
  "lazy-load-results-bq": false,
  "legacy-git-sync": false,
  "log-dbt-metadata-counts": false,
  "logic-session-timeout-extension": false,
  "magic-deflection": false,
  "magic-deflection-cell-limit": 0,
  "magic-deflection-embed": false,
  "magic-esignature": false,
  "magic-example-table-prompts": 0,
  "magic-example-table-prompts-query-threshold": 0,
  "magic-explore": false,
  "magic-explore-chat-config": defaultMagicExploreChatConfig,
  "magic-explore-edit": false,
  "magic-explore-edit-logic-view": false,
  "magic-explore-guess-primary-key-limit": 10,
  "magic-explore-planexec-retry-count": 0,
  "magic-lance-project-retrieval-limit": 30,
  "magic-lance-project-retrieval-vector-distance-threshold": 0,
  "magic-max-other-dfs": 25,
  "magic-notebook-duplicate-legacy-vis-tools": false,
  "magic-notebook-logic-view-config": defaultMagicNotebookLogicViewConfig,
  "magic-notebook-logic-view-with-temporal": false,
  "magic-slack-bot": false,
  "magic-smoke-test-single-cell": false,
  "magic-suggested-endorsements": false,
  "magic-suggested-prompts-pd-events-limit": 100,
  "magic-suggested-prompts-pd-limit": 20,
  "magic-summary": false,
  "magic-table-formatting-method": "YAML_LIKE",
  "magic-table-retrieval-columns-per-table-limit": 1000,
  "magic-table-retrieval-desc-similarity-threshold": 0.9,
  "manual-work-os-syncs": true,
  "map-us-county-data": false,
  "mark-orgs-from-wiz-issues": false,
  "max-attempts-wait-for-es-task-completion": 10,
  "max-dynamic-list-size": 10_000,
  "max-fstr-results": 30,
  "max-kernels-per-org": 500,
  "max-kernels-per-user": 25,
  "max-semantic-datasets-for-dataset-groups-component": 50,
  "max-signup-limit": 10,
  "max-untrusted-kernels": -1, // unlimited
  "maybe-embed-hex-delay-ms": 600000, // 10 minutes
  "mcp-get-thread-tool-config": {
    maxThreadStatusChecks: 10,
    threadStatusCheckIntervalMs: 2000,
  },
  "mcp-server": false,
  "minerva-circuit-breaker-enabled": false,
  "minerva-epoch": 0,
  "model-feature-config": {
    // Every model feature is empty by default for now
    features: typedObjectKeys(ModelFeatureKey).reduce(
      (acc, key) => {
        acc[key] = [];
        return acc;
      },
      {} as Record<ModelFeatureKey, MagicProviderConfig[]>,
    ),
  } satisfies ModelFeatureConfig,
  "new-onboarding-experience-threads": false,
  "new-seat-onboarding-experience": false,
  "no-max-signups": false,
  "no-op-garbage-collector-queues": false,
  "oauth-collab-always-expose-outputs": false,
  "oauth-dc-service-account-schema": false,
  "oauth-schemas-cache-enabled": true,
  "online-thread-eval-ui": false,
  "online-thread-evals": false,
  "only-add-code-cells-on-first-run": false,
  "open-ai-rate-limit-config": DEFAULT_OPEN_AI_RATE_LIMIT_CONFIG,
  "optimize-explore-hql-date-filters": false,
  "orphaned-file-deletion-job-settings": {},
  "output-content-read-dynamo": false,
  "output-content-write-dynamo": false,
  "pandas-copy-on-write": false,
  "password-users": false,
  "pip-shim-suggest-uv": false,
  "prewarm-kernels-ux-enablement": false,
  "priority-cell-execution": false,
  "product-launch": false,
  "product-launch-demo-hide-beta-flags": false,
  "project-chrome-v-6": false,
  "prompt-injection-window-size": 10,
  "public-api-ban-config": { isBanned: false },
  "pylon-chat": false,
  "query-event-queue-enabled": false,
  "query-mode-default-on": false,
  "quick-search-query-config": DEFAULT_QUERY_CONFIG,
  "redis-reuse-connections": false,
  "reduce-default-logic-idle-timeout-datetime": {
    reduceDefaultTimeoutAfter: new Date(
      Date.now() + 1 * 24 * 60 * 60 * 1000,
    ).toISOString(), // sets default date to 1 day from now
  },
  "register-ready-pods": false,
  "reindex-es-batch-size": 500,
  "reindex-es-job-limit": 1000,
  "reindex-schema-rate-limit-duration": 1,
  "reindex-schema-rate-limit-points": 1,
  "remote-dataframes-deprecated": false,
  "reserve-kernels-for-interactive":
    DEFAULT_RESERVE_KERNELS_FOR_INTERACTIVE_CONFIG,
  "resource-sync-to-compute-heartbeat-enabled": false,
  "run-project-api-rate-limit-config":
    DEFAULT_RUN_PROJECT_API_RATE_LIMIT_CONFIG,
  "run-selected-sql": false,
  "scheduled-run-concurrency": 1,
  "scheduled-run-delay": 60000,
  "scheduled-run-stop-button": false,
  "schema-chunk-size": 1000,
  "schema-refresh-data-service-batch-size": 10_000,
  "schema-refresh-dfs-download-finds-orphans": false,
  "schema-refresh-disable-gc": false,
  "schema-refresh-download-grants-rate-limit-config": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-download-schema-batch-size": 100,
  "schema-refresh-gc-batch-size": 100,
  "schema-refresh-gc-delay-ms": 1000,
  "schema-refresh-get-orphaned-entities-batch-size": 1000,
  "schema-refresh-get-schema-batch-size": 1000,
  "schema-refresh-interrupt": "NO_INTERRUPT",
  "schema-refresh-max-column-count": 2_000_00,
  "schema-refresh-rate-limit-duration": 1,
  "schema-refresh-rate-limit-points": 1,
  "schema-refresh-rate-limit-points-dfs": 1,
  "schema-refresh-update-db-batch-size": 5000,
  "schema-refresh-update-es-rate-limiter": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-update-es-table-column-batch-size": 10,
  "schema-refresh-update-lance-batch-size": 5000,
  "schema-refresh-use-get-datasource-entity-endpoints": true,
  "schema-refresh-v3-rate-limiting": DEFAULT_SCHEMA_REFRESH_RATE_LIMIT_CONFIG,
  "screenshot-extra-timeout": 5000,
  "screenshot-max-height": 15000,
  "screenshot-max-retries": 1,
  "search-workspace-rules-tool": false,
  "semantic-authoring-agent-with-temporal": false,
  "semantic-authoring-history": false,
  "semantic-ingest-es-rate-limit-duration": {
    duration: 1,
    points: 5,
  },
  "semantic-sql-tool": false,
  "semantic-views": false,
  "semantic-workbench-typeahead": false,
  "sidecar-batch-debounce-ms": 0,
  "sidecar-execution-events": false,
  "sidecar-health-monitoring-interval": 30_000,
  "sidecar-mp-concurrency-limit": 25,
  "sidecar-output-batch-debounce-ms": 0,
  "signed-embedding-api-rate-limit": 20,
  "skip-mp-ops-on-noninteractive-runs": false,
  "skip-old-sidecar-requests": false,
  "slack-channel-query-limit": 30,
  "slack-files-read": false,
  "slack-integration-v2": false,
  "slack-interactivity": false,
  "smoke-test-cleanup-cron": "30 * * * *", // default to every 30 min past the hour
  "smoke-test-cleanup-limit": 50,
  "sql-cell-query-mode-async": true,
  "st-prewarmed-defaults": NoPrewarmedImages,
  "store-embedded-api-calls": false,
  "stream-motherduck-results": true,
  "stream-schema-grant-data": false,
  "strip-output-contents-redux": DEFAULT_STRIP_OUTPUT_CONTENTS_CONFIG,
  "stripe-check-events-minimum-ms": 0,
  "supernova-a-a": false,
  "support-user-sharing-cleanup-settings":
    defaultSupportUserSharingCleanupSettings,
  "table-deletion-queue-batch-size": 1000,
  "table-deletion-queue-cron": "*/10 * * * *", // default to every 10 minutes
  "table-store-hex-versioning-enabled": true,
  "temporal-worker-concurrency-config": defaultTemporalWorkerConcurrencyConfig,
  "thread-inspector-sidebar": false,
  "threads-copy-download-viz": false,
  "threads-data-sources": false,
  "threads-kernel-idle-timeout-ms": null,
  "threads-mention-semantic-models": false,
  "threads-observability-page": false,
  "threads-question-router": false,
  "threads-view-project-sql": false,
  "threads-with-temporal": false,
  "trino-session-user-passthrough": false,
  "typeahead-v-2": {
    enabledPython: false,
    enabledSql: false,
    modelKeys: {
      dev: "",
      eu: "",
      us: "",
    },
  },
  "use-create-database-v2": false,
  "use-custom-kernels-only": false,
  "use-in-house-ck": false,
  "use-only-register-ready-pods": false,
  "use-replica-db-if-available": false,
  "user-rules-notebook-agent": false,
  "usercheck-domain-denial": false,
  "websocket-auth-caching": { timeout: null },
  "websocket-rate-limits": {},
  "workspace-invite-links": false,
  "workspace-search-enable-category-status-boost": false,
  ...NewUserFeatureFlagDefaults,
};

export enum FlagConfigOverrideSet {
  DEV_EXAMPLE = "DEV_EXAMPLE",
  DEV_EXAMPLE_2 = "DEV_EXAMPLE_2",
  JUPYTER = "JUPYTER",
}

export const FlagConfigOverride: Record<
  FlagConfigOverrideSet,
  Partial<FeatureFlagSet>
> = {
  DEV_EXAMPLE: {
    "experimentation-test": true,
  },
  DEV_EXAMPLE_2: {
    "experimentation-test": false,
  },
  JUPYTER: {},
};

export type FeatureFlagOverrideFn<T> = ({
  email,
  env,
  orgId,
  stackId,
}: {
  email?: string;
  env?: ProductionEnvironment;
  orgId?: OrgId;
  stackId?: string;
}) => T | undefined;
export type FeatureFlagOverride<T> = T | FeatureFlagOverrideFn<T>;

export type FeatureFlagOverrides = {
  [K in keyof FeatureFlagSet]?: FeatureFlagOverride<FeatureFlagSet[K]>;
};

// When we explicitly disable launch darkly, we include a set of offline
// overrides to manage behavior. This is specifically relevant to running E2E
// tests, where we want to avoid inflating our MAUs in LD but still support
// having multiple conditions under test.
export const LaunchDarklyDisabledOverrides: FeatureFlagOverrides = {
  "additional-mariadb-params": true,
  "allow-duplication-for-project": true,
  "allow-embedded-user": true,
  "allow-public-domain-signups": false,
  "app-state-persistence": true,
  "auto-rebuild-custom-images-delay": 250,
  "auto-rebuild-docker-images": false,
  "auto-rebuild-docker-images-cron-job": true, // This controls if the CRON job itself runs
  // Hardcode the python version used---in CI, we only install the 3.11 environment
  "base-kernel-image-gate": {
    defaultPythonKernelImage: "python311",
    enabled: ["python311"],
    python39NullCutoffDate: "2025-03-30T00:00:00.000Z",
  },
  "bull-queue-slow-job-threshold-override": {},
  "chain-sql-engine": "sqlparse",
  "compute-action-garbage-collector-settings":
    defaultComputeActionGarbageCollectorSettings,
  "compute-heartbeat-send-to-segment-enabled": false,
  "compute-heartbeat-sync-to-stripe-cron-schedule": "*/5 * * * *", // default to every 5 minutes
  "compute-heartbeat-sync-to-stripe-mode": "test",
  "compute-heartbeat-sync-to-stripe-status": "JOB_DISABLED",
  "consolidated-kernel-initialization": true,
  "cp-diff-tracking-cron-schedule": "*/1 * * * *",
  "cp-diff-tracking-delay-threshold-ms": 100,
  "cp-diff-tracking-enabled": false,
  "cp-kernel-management": DefaultKernelManagementCPMigrationState,
  "custom-kernels-ui": false,
  "custom-kernels-ui-v2": false,
  "data-conn-diff-schema-update": false,
  "data-conn-diff-schema-update-threshold": 0.1,
  "data-conn-insert-chunk-size": 1000,
  "data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "data-conn-tree-schema": false,
  "database-query-permissions": true,
  "databricks-jdbc-upgrade": true,
  "dbt-data-conn-sleep-times": { sleepEvery: 10000, sleepForMs: 10 },
  "dc-schema-filter-ui": false,
  "debug-gh-sync": true,
  "delete-app-session-api-client-on-kill": false,
  "disable-agent-chat-garbage-collector": false,
  "disable-ao-garbage-collector": false,
  "disable-embedded-user-api-client-garbage-collector": false,
  "disable-hex-garbage-collector": false,
  "disable-hex-version-default-app-session-garbage-collector": false,
  "disable-presigned-embed-url-garbage-collector": false,
  "disable-workspace-sharing": false,
  "emit-dbt-data-size-metric": false,
  "emit-schema-size-metric": false,
  "enable-api-enhancements": false,
  "enable-delete-pods-from-wiz-issues": false,
  "enable-reindex-schema": false,
  "enable-rerank-search-users": false,
  "enable-run-project-with-trace": false,
  "enable-schema-refresh-download-grants": false,
  "enable-sql-completion-logs": false,
  "enforce-compute-spend-limits": false,
  "es-bulk-delete-chunk-size": 300,
  "es-bulk-timeout-schema-refresh": "1m",
  "es-delete-all-datasources-delay-ms": 10000,
  "es-delete-by-query-timeout": "1m",
  "es-index-config": {
    datasourceSchemas: {
      backfillIndexes: ["datasource_schemas-v0"],
      readerIndex: "datasource_schemas-v0",
      writerIndexes: ["datasource_schemas-v0"],
    },
    projects: {
      backfillIndexes: ["projects"],
      readerIndex: "projects",
      writerIndexes: ["projects"],
    },
  },
  "es-index-reader-config": {
    datasourceSchemas: "datasource_schemas-v0",
    projects: "projects",
  },
  "es-reindex-config": {
    initialIdOffset: {},
  },
  "es-sync-hex-chunk-size": 10,
  "es-sync-hex-job-limit": 100,
  "explorable-apps": false,
  "explore-app-cell-view-data": false,
  "explore-backed-charts": false,
  "explore-backed-pivots": true,
  "explore-cancel-button": false,
  "filter-non-endorsed-tables-for-magic-explore": false,
  "force-redefinition-edges": true,
  "fstr-timeout-ms": 2000,
  "git-package-import-v2": false,
  "graphview-update": false,
  "hex-to-es-sync-mode": HexToESSyncMode.DISABLED,
  "hybrid-vega-renderer": true,
  "intermediate-state-caching": false,
  "internal-test-collection-job-delay-ms": 5000,
  "internal-test-collection-queue": DEFAULT_INTERNAL_TEST_COLLECTION_QUEUE,
  "kernel-lifetime-stats": false,
  "lance_db-sync-limiter-settings": defaultLanceSyncQueueLimiterSettings,
  "lance-db-admin-only-backfill-ui": false,
  "lance-db-data-conn-backfill-data-conn-to-skip": [],
  "lance-db-data-conn-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-data-conn-backfill-limit": 0,
  "lance-db-data-conn-backfill-retry-limit": 1,
  "lance-db-data-conn-errored-actions-limit": 0,
  "lance-db-data-conn-stuck-in-backfilling-limit": 10,
  "lance-db-hex-backfill": false,
  "lance-db-hex-backfill-excluded-orgs": [],
  "lance-db-hex-backfill-frequency-ms": 5 * 60 * 1000, // 5 minutes
  "lance-db-hex-backfill-limit": 0,
  "lance-db-hex-errored-actions-limit": 0,
  "lance-db-hex-stuck-in-backfilling-limit": 0,
  "lance-db-hex-stuck-in-processing-limit": 0,
  "lance-db-long-running-action-threshold-hours": 12,
  "lance-db-sync-batch-lance-actions-queue-interval-ms": 5000,
  "lance-db-sync-batch-size": 50000,
  "lance-db-table-retrieval-limit": 30,
  "lance-db-table-retrieval-minimum-result-size": 4,
  "lance-db-table-retrieval-timeout-ms": 5000,
  "lance-db-un-indexed-rows-threshold": 10_000,
  "lance-db-where-batch-size": 10000,
  "lance-db-write-timeout-ms": 10000,
  "legacy-git-sync": false,
  "magic-deflection": false,
  "magic-deflection-cell-limit": 0,
  "magic-deflection-embed": false,
  "magic-esignature": false,
  "magic-example-table-prompts": 0,
  "magic-example-table-prompts-query-threshold": 0,
  "magic-explore": false,
  "magic-explore-edit": false,
  "magic-explore-edit-logic-view": false,
  "magic-explore-planexec-retry-count": 0,
  "magic-lance-project-retrieval-limit": 30,
  "magic-lance-project-retrieval-vector-distance-threshold": 0,
  "magic-max-other-dfs": 25,
  "magic-smoke-test-single-cell": false,
  "magic-suggested-endorsements": false,
  "magic-summary": false,
  "magic-table-retrieval-columns-per-table-limit": 1000,
  "magic-table-retrieval-desc-similarity-threshold": 0.9,
  "manual-work-os-syncs": true,
  "mark-orgs-from-wiz-issues": false,
  "max-attempts-wait-for-es-task-completion": 10,
  "max-fstr-results": 30,
  "max-signup-limit": 10,
  "maybe-embed-hex-delay-ms": 600000, // 10 minutes
  "no-max-signups": false,
  "no-op-garbage-collector-queues": false,
  "only-add-code-cells-on-first-run": true,
  "optimize-explore-hql-date-filters": false,
  "pandas-copy-on-write": true,
  "password-users": false,
  "prewarm-kernels-ux-enablement": false,
  "priority-cell-execution": true,
  "prompt-injection-window-size": 10,
  "query-mode-default-on": false,
  "quick-search-query-config": DEFAULT_QUERY_CONFIG,
  "redis-reuse-connections": false,
  "register-ready-pods": false,
  "reindex-es-batch-size": 500,
  "reindex-es-job-limit": 1000,
  "reindex-schema-rate-limit-duration": 1,
  "reindex-schema-rate-limit-points": 1,
  "resource-sync-to-compute-heartbeat-enabled": false,
  "schema-chunk-size": 1000,
  "schema-refresh-data-service-batch-size": 10_000,
  "schema-refresh-disable-gc": false,
  "schema-refresh-download-grants-rate-limit-config": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-download-schema-batch-size": 100,
  "schema-refresh-gc-batch-size": 100,
  "schema-refresh-gc-delay-ms": 1000,
  "schema-refresh-get-orphaned-entities-batch-size": 1000,
  "schema-refresh-get-schema-batch-size": 1000,
  "schema-refresh-interrupt": "NO_INTERRUPT",
  "schema-refresh-max-column-count": 2_000_000,
  "schema-refresh-rate-limit-duration": 1,
  "schema-refresh-rate-limit-points": 1,
  "schema-refresh-update-db-batch-size": 5000,
  "schema-refresh-update-es-rate-limiter": {
    batchSize: 2_000,
    duration: 1,
    points: 1,
  },
  "schema-refresh-update-es-table-column-batch-size": 10,
  "schema-refresh-update-lance-batch-size": 5000,
  "schema-refresh-use-get-datasource-entity-endpoints": true,
  "screenshot-extra-timeout": 5000,
  "screenshot-max-height": 15000,
  "screenshot-max-retries": 1,
  "semantic-sql-tool": false,
  "skip-old-sidecar-requests": false,
  "slack-channel-query-limit": 30,
  "st-prewarmed-defaults": NoPrewarmedImages,
  "trino-session-user-passthrough": false,
  "use-create-database-v2": false,
  "use-in-house-ck": false,
  "use-only-register-ready-pods": false,
  "use-replica-db-if-available": false,
  "usercheck-domain-denial": false,
};

export const AppFeatureFlagRuntype = R.Union(
  ...(typedObjectKeys(DEFAULT_FLAGS).reduce((acc, flag) => {
    if (!isNewUserFeature(flag)) {
      acc.push(R.Literal(flag));
    }
    return acc;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [] as any[]) as any),
) as R.Runtype<AppFeatureFlag>;

/**
 *
 * @param flagValue the value of the LD flag
 * @param date the date to compare against the flag value
 * @returns true ("enabled") if date >= flagValue, false ("disabled") otherwise
   (or if flag value is explicitly set to "DISABLED") * Will throw error if
   unable to parse date from LD flag value.
 */
export const evaluateMinDateFlag = (
  flagValue: string | undefined,
  date: Date,
): boolean => {
  if (flagValue == null || flagValue === "DISABLED") {
    return false;
  }

  const minDate = new Date(flagValue);
  if (isNaN(minDate.getTime())) {
    throw new Error("Invalid date");
  }

  return date.getTime() >= minDate.getTime();
};

type AppFeatureFlagSet = Omit<FeatureFlagSet, FlaggedNewUserFeature>;

// There are certain flags that correspond to "new user" features, which should
// only be evaluated when a user first logs into Hex. We are generating this
// type that excludes those flags values to ensure that those flags are not
// evaluated outside of first login.
export type AppFeatureFlag = keyof AppFeatureFlagSet;

// These are flags exported to the python kernel and accessible from the Python
// kernel.
export const kernelFeatureFlags = [
  "disable-large-pivot-csv",
  "duckdb-query-arrow-datasets",
  "remote-dataframes-deprecated",
] as const satisfies (keyof FeatureFlagSet)[];
